import { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';

function getInitials(fullName: string | null | undefined, email: string | null | undefined): string {
  if (fullName) {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return fullName.slice(0, 2).toUpperCase();
  }
  return email ? email.slice(0, 2).toUpperCase() : 'U';
}

export default function AyarlarPage() {
  const { isDark, toggleTheme } = useTheme();
  const { epUser, logout } = useAuth();
  const [notifications, setNotifications] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [language, setLanguage] = useState('tr');

  const displayName = epUser?.full_name || epUser?.email?.split('@')[0] || 'Kullanıcı';
  const initials = getInitials(epUser?.full_name, epUser?.email);

  return (
    <div className="space-y-4 max-w-2xl">
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gray-500">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-settings-3-line text-white text-lg" />
          </div>
        </div>
        <div>
          <h1 className={`text-lg font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
            Ayarlar
          </h1>
          <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
            Sistem ve hesap ayarları
          </p>
        </div>
      </div>

      {/* Appearance */}
      <div className={`rounded-xl p-4 md:p-5 ${isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'}`}>
        <h2 className={`text-sm font-semibold mb-4 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
          Görünüm
        </h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                Koyu Tema
              </p>
              <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                Panelin koyu modda görünmesini sağlar
              </p>
            </div>
            <button
              onClick={toggleTheme}
              className={`relative w-12 h-7 rounded-full transition-colors ${
                isDark ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <div
                className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform ${
                  isDark ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              >
                <div className="w-full h-full flex items-center justify-center">
                  <i className={`${isDark ? 'ri-moon-line' : 'ri-sun-line'} text-xs text-gray-600`} />
                </div>
              </div>
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                Dil
              </p>
              <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                Panel dili
              </p>
            </div>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className={`px-3 py-2 rounded-lg text-sm border outline-none ${
                isDark ? 'bg-dark-bg border-dark-border text-dark-text' : 'bg-light-bg border-light-border text-light-text'
              }`}
            >
              <option value="tr">Türkçe</option>
              <option value="en">English</option>
            </select>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className={`rounded-xl p-4 md:p-5 ${isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'}`}>
        <h2 className={`text-sm font-semibold mb-4 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
          Bildirimler
        </h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                Push Bildirimleri
              </p>
              <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                Hatırlatıcı ve güncellemeler için bildirim
              </p>
            </div>
            <button
              onClick={() => setNotifications(!notifications)}
              className={`relative w-12 h-7 rounded-full transition-colors ${
                notifications ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <div
                className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform ${
                  notifications ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                Sesli Uyarı
              </p>
              <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                Yeni hatırlatma geldiğinde ses çal
              </p>
            </div>
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`relative w-12 h-7 rounded-full transition-colors ${
                soundEnabled ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <div
                className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform ${
                  soundEnabled ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Account */}
      <div className={`rounded-xl p-4 md:p-5 ${isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'}`}>
        <h2 className={`text-sm font-semibold mb-4 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
          Hesap
        </h2>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center">
              <span className="text-white font-bold">{initials}</span>
            </div>
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                {displayName}
              </p>
              <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                {epUser?.email || 'email@example.com'}
              </p>
            </div>
          </div>

          <div className={`pt-4 border-t ${isDark ? 'border-dark-border' : 'border-light-border'}`}>
            <button
              onClick={logout}
              className="w-full py-2.5 text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            >
              Çıkış Yap
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}